import { useCallback, useEffect } from "react";
import { FormProvider, SubmitHandler, useForm, useWatch } from "react-hook-form";

import FlashContext from "components/FlashMessages/FlashContext";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import FormArrayField from "ds/components/Form/ArrayField";
import FormField from "ds/components/Form/Field";
import FormFieldRowWrapper from "ds/components/Form/Field/RowWrapper";
import Input from "ds/components/Input";
import Typography from "ds/components/Typography";
import useBlockNavigationModal from "hooks/useBlockNavigationModal";
import useTypedContext from "hooks/useTypedContext";
import { validateRedirectAllowlistHost } from "utils/formValidators";

import { GET_OAUTH_REDIRECT_ALLOWLIST } from "./gql";
import {
  EMPTY_VALUE,
  getFilledHosts,
  getFormDefaultValues,
  MAX_HOSTS,
  OauthRedirectAllowlistFormFields,
  validateUniqueHost,
} from "./helpers";
import useSetOauthRedirectAllowlist from "./useSetAllowlist";

type OauthRedirectAllowlistFormProps = {
  currentHosts: string[] | undefined;
};

const OauthRedirectAllowlistForm = ({ currentHosts }: OauthRedirectAllowlistFormProps) => {
  const { reportSuccess } = useTypedContext(FlashContext);

  const form = useForm<OauthRedirectAllowlistFormFields>({
    defaultValues: getFormDefaultValues(currentHosts),
    mode: "onChange",
  });

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { isDirty, isValid, errors },
  } = form;

  useEffect(() => {
    reset(getFormDefaultValues(currentHosts));
  }, [reset, currentHosts]);

  useBlockNavigationModal({ isNavigationBlocked: isDirty });

  const [setAllowlist, { loading }] = useSetOauthRedirectAllowlist({
    refetchQueries: [GET_OAUTH_REDIRECT_ALLOWLIST],
  });

  const onSubmit: SubmitHandler<OauthRedirectAllowlistFormFields> = useCallback(
    async (formFields) => {
      const hosts = getFilledHosts(formFields.hosts);

      await setAllowlist({ hosts }, (data) => {
        const stored = data?.accountSetOAuthRedirectAllowlist;

        if (stored) {
          reset(getFormDefaultValues(stored));
          reportSuccess({ message: "OAuth redirect allowlist has been updated" });
        }
      });
    },
    [setAllowlist, reset, reportSuccess]
  );

  const handleEnter = (callback?: () => void) => (e: React.KeyboardEvent) => {
    if (callback && e.key === "Enter") {
      e.preventDefault();
      callback();
    }
  };

  const hosts = useWatch({ control, name: "hosts" });

  // To be able to clear the allowlist we need to allow the user to submit an empty list.
  const isClearing = isDirty && getFilledHosts(hosts).length === 0;
  const hasIncompleteHost = hosts.some(
    (host, index) => !host.value.trim() || !!errors?.hosts?.[index]?.value
  );

  return (
    <FormProvider {...form}>
      <Box direction="column" gap="large" fullWidth>
        <Typography variant="p-body3" tag="p" color="secondary">
          Bare hostname or a single leading *. wildcard. No scheme, port, or path.
        </Typography>

        <FormArrayField<OauthRedirectAllowlistFormFields, "hosts">
          name="hosts"
          addButtonLabel="Add another host"
          emptyValue={EMPTY_VALUE}
          isAddDisabled={hosts.length >= MAX_HOSTS || hasIncompleteHost}
        >
          {({ field, index, addEmptyItem, suffix }) => (
            <FormField
              key={field.id}
              error={errors?.hosts?.[index]?.value?.message}
              fullWidth
              noMargin
            >
              {({ ariaInputProps }) => (
                <FormFieldRowWrapper>
                  <Input
                    aria-label={`Redirect host ${index + 1}`}
                    placeholder="app.example.com or *.example.com"
                    onKeyDown={handleEnter(addEmptyItem)}
                    error={!!errors?.hosts?.[index]?.value?.message}
                    {...register(`hosts.${index}.value`, {
                      setValueAs: (value) => value.trim(),
                      validate: {
                        format: validateRedirectAllowlistHost,
                        unique: validateUniqueHost(index),
                      },
                      required: "Host is required",
                    })}
                    {...ariaInputProps}
                  />
                  {suffix}
                </FormFieldRowWrapper>
              )}
            </FormField>
          )}
        </FormArrayField>

        {hosts.length >= MAX_HOSTS && (
          <Typography variant="p-body3" tag="p" color="secondary">
            You&apos;ve reached the maximum of {MAX_HOSTS} hosts.
          </Typography>
        )}
      </Box>

      <Box justify="end" gap="large" margin="x-large 0 0">
        {isDirty && (
          <Button variant="secondary" onPress={() => reset()} disabled={loading}>
            Cancel
          </Button>
        )}
        <Button
          variant="primary"
          onPress={() => (isClearing ? onSubmit({ hosts: [] }) : handleSubmit(onSubmit)())}
          loading={loading}
          disabled={loading || !isDirty || (!isValid && !isClearing)}
        >
          Save
        </Button>
      </Box>
    </FormProvider>
  );
};

export default OauthRedirectAllowlistForm;
